import {
  Box,
  Card,
  CardActionArea,
  Divider,
  Typography,
  makeStyles,
  useTheme,
  Toolbar as UIToolbar,
} from '@material-ui/core';
import { Children, memo, useMemo } from 'react';

import Hidden from './HiddenSSRCompatible';
import IconTypography from './IconTypography';

const _variantToBgColor = (variant) => {
  switch (variant) {
    case 'success':
      return 'success.main';
    case 'warning':
      return 'warning.main';
    case 'danger':
      return 'error.light';
    case 'custom':
      return '#A04392';
    default:
      return 'info.light';
  }
};

export const SmallCardRow = memo(function SmallCardRow({ children, ...props }) {
  return (
    <Box
      display="flex"
      alignItems="center"
      {...props}
      color="text.secondary"
      fontSize="14px"
    >
      {Children.toArray(children).map((child, index) => (
        <Box key={index} flexGrow={index === 0 ? 1 : 0}>
          {child}
        </Box>
      ))}
    </Box>
  );
});

export const CardRow = memo(function CardRow({ children, ...props }) {
  return (
    <Box
      display="flex"
      alignItems="center"
      {...props}
      color="text.secondary"
      fontSize="body1.fontSize"
    >
      {Children.toArray(children).map((child, index) => (
        <Box key={index} flexGrow={index === 0 ? 1 : 0}>
          {child}
        </Box>
      ))}
    </Box>
  );
});

export const PageCard = memo(function PageCard({
  variant,
  Icon,
  title,
  info,
  children,
}) {
  const bgColor = useMemo(() => _variantToBgColor(variant), [variant]);

  return (
    <>
      <Hidden xsDown>
        <Box
          position="relative"
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          p={1}
          bgcolor={bgColor}
          color="primary.contrastText"
          width="100%"
          borderRadius="borderRadius"
          flexGrow={1}
        >
          <Box fontSize="caption.fontSize">{title}</Box>

          <Box display="flex" alignItems="center" justifyContent="center" fontSize="h4.fontSize">
            <Box display="flex" mr={1}>
              <Icon fontSize="inherit" />
            </Box>
            <Box py={1}>{children}</Box>
          </Box>

          {!!info && <Box fontSize="caption.fontSize">{info}</Box>}
        </Box>
      </Hidden>
      <Hidden smUp>
        <Box
          position="relative"
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          p={1}
          bgcolor={bgColor}
          color="primary.contrastText"
          width="100%"
          height="100%"
          borderRadius="borderRadius"
          flexGrow={1}
        >
          <Box fontSize="caption.fontSize">{title}</Box>

          <Box display="flex" alignItems="center" justifyContent="center" fontSize="caption.fontSize">
            <Box display="flex" mr={1}>
              <Icon fontSize="inherit" />
            </Box>
            <Box py={1}>{children}</Box>
          </Box>

          {!!info && <Box fontSize="caption.fontSize">{info}</Box>}
        </Box>
      </Hidden>
    </>
  );
});

const CardContent = ({ Toolbar, info, minHeight, children }) => (
  <Box pt={1.8}>
    {!!Toolbar && (
      <>
        <UIToolbar>
          <Box width="100%" display="flex" justifyContent="flex-end">
            {Toolbar}
          </Box>
        </UIToolbar>
        <Box pb={1}>
          <Divider variant="middle" />
        </Box>
      </>
    )}

    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      px={1.8}
      minHeight={minHeight}
    >
      {children}
    </Box>

    {info && (
      <>
        <Divider />
        <Box p={1}>
          <Typography component="div" color="textSecondary" variant="caption">
            {info}
          </Typography>
        </Box>
      </>
    )}
  </Box>
);

export const HighlightCard = memo(function HighlightCard({
  variant,
  Icon,
  title,
  info,
  Toolbar,
  minHeight = 'none',
  onClick,
  children,
  bgColor,
  paddingBottom,
}) {
  const theme = useTheme();
  const useStyles = makeStyles((theme) => ({
    root: {
      borderRadius: "20px",
      backgroundColor: bgColor,
      height: "100%",
      boxShadow: "none",
      paddingBottom: paddingBottom,
    },
  }));
  const classes = useStyles();

  return (
    <Box position="relative" sx={{ height: '100%' }}>
      {onClick ? (
        <Card className={classes.root} onClick={onClick} borderRadius={6}>
          <CardActionArea>
            <CardContent info={info} Toolbar={Toolbar}>
              {children}
            </CardContent>
          </CardActionArea>
        </Card>
      ) : (
        <Card className={classes.root} borderRadius={6}>
          <CardContent info={info} Toolbar={Toolbar} minHeight={minHeight}>
            {children}
          </CardContent>
        </Card>
      )}
    </Box>
  );
});

export const DashboardCard = memo(function DashboardCard({
  variant,
  Icon,
  title,
  info,
  Toolbar,
  minHeight = 'none',
  onClick,
  children,
  ...props
}) {
  return (
    <Box position="relative" {...props}>
      {onClick ? (
        <Card onClick={onClick}>
          <CardActionArea>
            <CardContent info={info} Toolbar={Toolbar}>
              {children}
            </CardContent>
          </CardActionArea>
        </Card>
      ) : (
        <Card>
          <CardContent info={info} Toolbar={Toolbar} minHeight={minHeight}>
            {children}
          </CardContent>
        </Card>
      )}
      <Box
        display="flex"
        alignItems="center"
        position="absolute"
        top={-12}
        left={10}
        right={10}
        p={0.5}
        color="primary.contrastText"
        bgcolor={_variantToBgColor(variant)}
      >
        <IconTypography Icon={Icon} noWrap>
          {title}
        </IconTypography>
      </Box>
    </Box>
  );
});

export const PageInfoCard = memo(function PageInfoCard({
  variant,
  Icon,
  title,
  info,
  Toolbar,
  onClick,
  children,
  ...props
}) {
  return (
    <DashboardCard
      variant={variant}
      Icon={Icon}
      title={title}
      info={info}
      Toolbar={Toolbar}
      minHeight={200}
      onClick={onClick}
      {...props}
    >
      {children}
    </DashboardCard>
  );
});

export const FigureCardBold = memo(function FigureCardBold({ children, variant, fontColor, alignText = "left" }) {
  const theme = useTheme();
  const useStyles = makeStyles((theme) => ({
    root: {
      fontWeight: "600",
    },
  }));

  const classes = useStyles();
  return (
    <Typography style={{ color: fontColor, textAlign: alignText }} className={classes.root} variant={variant}>
      {children}
    </Typography>
  )
});


export const PrimaryCard = memo(function HighlightCard({
  variant,
  Icon,
  title,
  info,
  Toolbar,
  minHeight = 'none',
  onClick,
  height = "100%",
  children,
  bgColor,
  className,
  ...props
}) {
  const theme = useTheme();
  const useStyles = makeStyles((theme) => ({
    root: {
      borderRadius: "20px",
      backgroundColor: bgColor,
      height: height,
      boxShadow: "none",
      marginBottom: "0px"
    },
  }));
  const classes = useStyles();

  return (
    <Card className={`${classes.root} ${className}`} borderRadius={6} {...props}>
      {children}
    </Card>
  );
});